import {
  Alert,
  AlertProps,
  Box,
  Button,
  FormControl,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { collection, doc, runTransaction, Timestamp } from 'firebase/firestore'
import { useMemo, useState } from 'react'
import { PublicUserExtended, useUsers } from '../../../providers/users'
import { firestore } from '../../firebaseInit'
import { useAuth } from '../../providers/auth'
import Loading from '../../views/loading'
import UsersAutocomplete from '../input/users-autocomplete'

const toCredits = (value: unknown) => {
  const credits = Number(value)
  return Number.isFinite(credits) ? credits : 0
}

type Props = {
  showHeading?: boolean
}

export default function CreditsAdd({ showHeading = true }: Props) {
  const { usersExt, loading: loadingUsers, error: errorUsers } = useUsers()
  const { user: currentUser } = useAuth()

  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string>('')
  const [user, setUser] = useState<PublicUserExtended | null>(null)
  const [creditAmount, setCreditAmount] = useState<string>('')
  const [currentCredits, setCurrentCredits] = useState<number>(0)
  const [snackbar, setSnackbar] = useState<Pick<
    AlertProps,
    'children' | 'severity'
  > | null>(null)

  const amount = Number(creditAmount)
  const nextCredits = useMemo(
    () =>
      Number.isFinite(amount) && amount > 0
        ? currentCredits + amount
        : currentCredits,
    [amount, currentCredits]
  )

  const handleSave = async () => {
    if (!user) return

    if (!Number.isInteger(amount) || amount <= 0) {
      setError('Ange ett positivt heltal')
      return
    }

    setLoading(true)
    setError('')

    try {
      const userRef = doc(firestore, 'users', user.uid)
      const creditEventRef = doc(collection(firestore, 'creditEvents'))

      const updatedCredits = await runTransaction(firestore, async (tx) => {
        const userSnap = await tx.get(userRef)
        const existingCredits = toCredits(userSnap.data()?.credits)
        const credits = existingCredits + amount

        tx.set(
          userRef,
          {
            credits,
            lastCreditAction: 'admin-topup',
            lastCreditUpdatedAt: Timestamp.now(),
          },
          { merge: true }
        )

        tx.set(creditEventRef, {
          type: 'topup',
          amount,
          userId: user.uid,
          userEmail: user.email,
          userDisplayName: user.displayName,
          createdAt: Timestamp.now(),
          createdBy: currentUser
            ? {
                uid: currentUser.uid,
                displayName: currentUser.displayName,
                email: currentUser.email,
              }
            : null,
        })

        return credits
      })

      setCurrentCredits(updatedCredits)
      setCreditAmount('')
      setSnackbar({
        children: `${amount} credits lades till för ${user.displayName}`,
        severity: 'success',
      })
    } catch (error) {
      console.log(error)
      setSnackbar({
        children: 'Credits kunde inte uppdateras',
        severity: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  const handleChangeCredits = (value: string) => {
    setCreditAmount(value)

    if (value === '') {
      setError('')
      return
    }

    const credits = Number(value)
    setError(
      Number.isInteger(credits) && credits > 0
        ? ''
        : 'Ange ett positivt heltal'
    )
  }

  if (loadingUsers) return <Loading />

  return (
    <Box mt={2}>
      {showHeading && (
        <>
          <Typography variant="h6" component="h2" gutterBottom>
            Credits
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Lägg till förköpta credits på en användares saldo.
          </Typography>
        </>
      )}
      <FormControl fullWidth variant="outlined">
        <UsersAutocomplete
          id="add-credits-to-users"
          key="add-credits-to-users"
          label="Användare"
          users={usersExt}
          usersLoading={loadingUsers}
          usersError={errorUsers!}
          componentLoading={loading}
          multiple={false}
          onChangeCallback={(e: any, value: PublicUserExtended | null) => {
            setUser(value)
            setCreditAmount('')
            setError('')
            setCurrentCredits(value ? toCredits(value.credits) : 0)
          }}
        />
        {user && (
          <Box mt={2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Nuvarande saldo"
                value={currentCredits}
                InputProps={{
                  readOnly: true,
                }}
              />
              <TextField
                onChange={(event) => handleChangeCredits(event.target.value)}
                label="Lägg till"
                value={creditAmount}
                variant="outlined"
                error={!!error}
                helperText={error || 'Antal köpta credits'}
                fullWidth
              />
              <TextField
                label="Nytt saldo"
                value={nextCredits}
                InputProps={{
                  readOnly: true,
                }}
              />
            </Stack>
            <Stack
              mt={2}
              direction="row"
              spacing={1}
              justifyContent="flex-end"
            >
              <Button
                disabled={!!error || !creditAmount || loading}
                onClick={handleSave}
                variant="contained"
              >
                Lägg till credits
              </Button>
            </Stack>
          </Box>
        )}
      </FormControl>
      {!!snackbar && (
        <Snackbar
          open
          onClose={() => setSnackbar(null)}
          autoHideDuration={6000}
        >
          <Alert {...snackbar} onClose={() => setSnackbar(null)} />
        </Snackbar>
      )}
    </Box>
  )
}
